import React, { useContext, useState } from "react";
import { Box, Chip, Button } from "@mui/material";
import { BADGE_COLORS, getBadgeStyles } from "../../../../components/ui/badgeColors";
import WarningIcon from "@mui/icons-material/Warning";
import { ModalAnadirEan, ModalConfirmacion } from "../modals";
import {StateContext} from "../../../../components/context/StateProvider";

/**
 * Fila de producto del DataGrid de punteo (slot `row` de ListOrder)
 */
const OrderProduct = (props) => {

    const {state, updateState, getNombreAlmacen} = useContext(StateContext);
    const [show, setShow] = useState(false);

    const handleForceCheck = () => setShow((previo) => !previo);

    const handleModalConfirmation = async () => {
        const unitsRegister = props.row.register + props.row.remaining;

        let refArticulo = props.row.ref;

        if (props.row.duplicate) {
            refArticulo += '-' + props.row.line;
        }

        await props.updateOrderAction(props.registerOrderAction, state, props.row.id, refArticulo, unitsRegister, new Date(), props.row.remaining, updateState, true, state.year);
        handleForceCheck();
    };

    // Determinar color de fondo según estado
    const getBackgroundColor = () => {
        if (props.read) return "transparent";
        return props.row.state ? BADGE_COLORS.GREEN.bg : BADGE_COLORS.RED.bg;
    };

    return (
            <Box
                sx={{
                    display: "flex",
                    width: "100%",
                    backgroundColor: getBackgroundColor(),
                    borderRadius: 0,
                    border: "1px solid #e9ecef",
                    borderTop: "none",
                    p: 1,
                    minHeight: 60,
                    alignItems: "center",
                    gap: 2,
                    "&:first-of-type": {
                        borderTop: "1px solid #e9ecef"
                    }
                }}
            >
                {/* Columna: Referencia + Badge + Botón */}
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        justifyContent: "center",
                        gap: 0.5,
                        position: "relative",
                        alignSelf: "stretch",
                        m: -1,
                        p: 1
                    }}
                >
                    {/* Fondo rojo para productos con serie */}
                    {!props.read && props.row.serie && (
                        <Box
                            sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: BADGE_COLORS.RED.border,
                                zIndex: 0
                            }}
                        />
                    )}

                    <Box sx={{ position: "relative", zIndex: 1, color: !props.read && props.row.serie ? "#fff" : "inherit" }}>
                        {props.row.ref}
                    </Box>
                    {props.row.manual && (
                        <Box sx={{ position: "relative", zIndex: 1 }}>
                            <Chip
                                label="Forzado"
                                size="small"
                                sx={getBadgeStyles(BADGE_COLORS.BLUE, { height: 20 })}
                            />
                        </Box>
                    )}
                    {!props.row.state && !props.read && !state.order.complete && (
                        <Box sx={{ mt: 0.5, position: "relative", zIndex: 1 }}>
                            <ModalConfirmacion
                                show={show}
                                onHide={handleForceCheck}
                                onConfirm={handleModalConfirmation}
                                title="Confirmar forzado"
                                titleMobile="Forzar"
                                text="¿Está seguro de que desea <strong>forzar el punteo</strong> de este producto?"
                                detalles={[
                                    { label: "Referencia", value: props.row.ref },
                                    { label: "Producto", value: props.row.name }
                                ]}
                                icon={WarningIcon}
                                confirmIcon={WarningIcon}
                                confirmLabel="Forzar punteo"
                                confirmLabelMobile="Forzar"
                            />
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleForceCheck}
                                sx={{
                                    p: 0.5,
                                    fontSize: 12,
                                    minWidth: "auto",
                                    textTransform: "none",
                                    fontWeight: 600,
                                    backgroundColor: "#fff",
                                    color: BADGE_COLORS.AMBER.text,
                                    borderColor: BADGE_COLORS.AMBER.border,
                                    "&:hover": {
                                        borderColor: BADGE_COLORS.AMBER.text,
                                        backgroundColor: BADGE_COLORS.AMBER.bg
                                    }
                                }}
                            >
                                Forzar punteo
                            </Button>
                        </Box>
                    )}
                </Box>

                {/* Columna: Código de barras */}
                <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 1 }}>
                    {props.row.ean.map(item => item.BARRAS + '-' + item.UNIDADES).join(', ')}
                    {props.row.units > 0 && !props.row.state && !props.read && !state.order.complete && (
                        <ModalAnadirEan productoId={props.row.id} updated={true} />
                    )}
                </Box>

                {/* Columna: Nombre + Stock */}
                <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                        {props.row.name}
                    </Box>
                    {Array.isArray(props.row.stock) && props.row.stock.length > 0 && !props.row.state && (
                        <Box>
                            {props.row.stock.map((item, index) => (
                                <Box key={index} sx={{ display: "flex", alignItems: "center" }}>
                                    <Box component="strong">
                                        <Box component="span" sx={{ mr: 1 }}>
                                            Stock {getNombreAlmacen(item.ALMACEN)}:
                                        </Box>
                                        <Box component="span">{item.FINAL}</Box>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>

                {/* Columna: Unidades */}
                <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
                    {props.row.units} {!props.read ? `/ ${props.row.remaining}` : ''}
                </Box>

                {/* Columna: Ubicación */}
                <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
                    {props.row.location}
                </Box>

                {/* Columna: Fecha (solo si no es read) */}
                {!props.read && (
                    <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
                        {props.row.registerDate}
                    </Box>
                )}
            </Box>
    );
};

export default OrderProduct;
